import {
  ReactElement,
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState
} from 'react'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { useAtom } from 'jotai'
import { useLocation, useNavigate } from 'react-router-dom'
import getMFEConfig from 'helpers/getMFEConfig'
import { makeAPaymentModalContainerId } from 'const/modalContainerId'
import { useAccountSummary, useCreditAccounts } from 'const/syfApiQueries'
import findAccount from 'helpers/findAccount'
import getHelpNumber from 'helpers/getHelpNumbers'
import isAccountTypeSetpayOrPaylater from 'helpers/isAccountTypeSetpayOrPaylater'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import triggerQualtrics from 'helpers/triggerQualtrics'
import useOpenActivity from 'hooks/useOpenActivity'
import routes from 'const/routes'
import { analyticsConfig } from '@syf-utils/analytics'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import ErrorBoundary from 'ui/patterns/ErrorBoundary'
import { createMFERuntimeBoundary } from 'newrelic/createMFERuntimeBoundary'
import { ModalHeader } from 'syf-component-library/ui/patterns/SlideInModalPortal'
import i18n from 'strings/i18n'
import { useIVASetChatStatus } from 'iva-react-web-ui'
import {
  type MAPFlowType,
  type MakeAPaymentHandle,
  type MakeAPaymentProps
} from './typings'
import {
  PaddedSlideInModalLoader,
  SlideInModalPortalWrapper
} from './subcomponents'

/* eslint-disable import/no-unresolved */
const PaymentModalRemote = lazy(() => import('make-payment-mfe/MakeAPayment'))
const PaymentModal = lazy(() =>
  import('make-a-payment').then((module) => ({ default: module.default }))
)

const {
  Runtime: PaymentModalRuntime,
  BuildTime: PaymentModalBuildTime,
  onRuntimeError
} = createMFERuntimeBoundary<MakeAPaymentProps, MakeAPaymentHandle>({
  mfe: 'make-a-payment',
  surface: 'modal',
  RuntimeComponent: PaymentModalRemote,
  BuildTimeComponent: PaymentModal
})

const pathToPageInfo = {
  [routes.ACCOUNT_SERVICES]: {
    PageFunction: 'account services',
    PageKind: 'account services'
  },
  [routes.ACTIVITY]: {
    PageFunction: 'activity',
    PageKind: 'activity'
  },
  // needs to be updated dynamically - currently updating with default info
  [routes.PROMOTIONS]: {
    PageFunction: 'promo_tab',
    PageKind: 'activity'
  }
}

const MakePaymentModal = (): ReactElement => {
  const [{ modalId, metadata }, updateModalStatus] = useAtom(modalStatusAtom)
  const [selectedAccount, setSelectedAccount] = useAtom(selectedAccountAtom)
  const mapRef = useRef<MakeAPaymentHandle>(null)
  const [isCloseInterstitialShown, setIsCloseInterstitialShown] =
    useState(false)

  const isModalOpen = modalId === 'MAP'

  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { data: accountSummary } = useAccountSummary(selectedAccount)
  const location = useLocation()
  const navigate = useNavigate()
  const openActivity = useOpenActivity()
  const isModalOpenRef = useRef(false)
  const { pointOfNeedChat } = useIVASetChatStatus()

  useEffect(() => {
    if (isModalOpenRef.current && !isModalOpen) {
      // update pageInfo based on the page where MAP is opened/closed
      const pageInfo = pathToPageInfo[location.pathname] ?? {
        PageFunction: 'dashboard',
        PageKind: 'multiaccount'
      }
      triggerAnalyticsEvent(pageInfo)
    }
    isModalOpenRef.current = isModalOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen])

  // defer loading make-a-payment and computing props until modal is open
  if (!isModalOpen) return null
  // early return if selectedAccount is not set, should be impossible but guarding against it nonetheless
  if (!selectedAccount) return null

  const account = findAccount(accounts, selectedAccount)
  const accountLast4 = account?.info?.accountLast4
  const clientId = accountSummary?.product?.clientId
  const { type: productType } = account?.product || {}
  const helpNumber = getHelpNumber(clientId, productType)
  const { hosts } = getMFEConfig('mfe')

  const lang = getLanguageFromStorage()

  const handleOpenChat = () => {
    pointOfNeedChat('', selectedAccount)
  }

  const handleClose = () => {
    updateModalStatus('', '')
  }

  const onDeepLink = (accountId: string, flowType: MAPFlowType) => {
    setSelectedAccount(accountId)
    updateModalStatus('MAP', makeAPaymentModalContainerId(accountId), {
      MAP: { flow: { type: flowType } }
    })
  }

  const props = {
    id: 'make-a-payment',
    analyticsConfig,
    accountLast4,
    containerId: makeAPaymentModalContainerId(''),
    isOpen: isModalOpen,
    handleClose,
    accountId: selectedAccount,
    lang,
    apiErrorCallback: (error: unknown) => error,
    hosts,
    helpNumber,
    onNavigateToActivity: () => {
      if (isAccountTypeSetpayOrPaylater(productType)) {
        openActivity({
          accountId: selectedAccount,
          onBeforeOpenModal: handleClose
        })
        return
      }
      navigate(routes.ACTIVITY)
    },
    onOpenChat: handleOpenChat,
    handleCloseOnConversion: () => {
      triggerQualtrics()
    },
    additionalProps: metadata?.MAP,
    onDeepLink
  }

  const isMakeAPaymentRuntimeEnabled = isFeatureFlagEnabled(
    'isMakeAPaymentRuntimeEnabled'
  )

  if (isMakeAPaymentRuntimeEnabled) {
    /*
     * When MAP is loading (Suspense fallback active), mapRef is null —
     * fall back to a raw close since no form state exists yet.
     */
    const requestClose = () => {
      if (mapRef.current) {
        mapRef.current.requestClose()
      } else {
        handleClose()
      }
    }

    return (
      <SlideInModalPortalWrapper
        ariaLabel="Make a Payment"
        containerId={props.containerId}
        escapeModalHandler={requestClose}
        id={props.id}
        isCloseInterstitialShown={isCloseInterstitialShown}
        isOpen={isModalOpen}
      >
        <ErrorBoundary
          customFallback={
            <PaymentModalBuildTime
              {...props}
              ref={mapRef}
              embedded={{
                onCloseInterstitialChange: setIsCloseInterstitialShown
              }}
              isRuntimeEnabled={isMakeAPaymentRuntimeEnabled}
            />
          }
          onError={onRuntimeError}
        >
          <Suspense
            fallback={
              <>
                <ModalHeader
                  content={i18n({ modal: 'makeAPaymentModalTitle' }) as string}
                  closeModal={requestClose}
                />
                <PaddedSlideInModalLoader />
              </>
            }
          >
            <PaymentModalRuntime
              {...props}
              ref={mapRef}
              embedded={{
                onCloseInterstitialChange: setIsCloseInterstitialShown
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </SlideInModalPortalWrapper>
    )
  }

  return <PaymentModalBuildTime {...props} isRuntimeEnabled={false} />
}

export default MakePaymentModal
